import React, { useEffect, useState } from "react";
import {
  Typography,
  Grid,
  TextField,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CardHeader,
  Box,
  Autocomplete,
} from "@mui/material";
import unsplash from "../api/unsplash";
import {
  LocalizationProvider,
  DatePicker,
  PickersDay,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterMoment as DateAdapter } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";

import {
  ComposedChart,
  Line,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  BarChart,
  LabelList,
  Cell,
} from "recharts";
import {
  DragHandle,
  KeyboardDoubleArrowDown,
  KeyboardDoubleArrowUp,
} from "@mui/icons-material";

const KpiMedicina = () => {
  const [date, setDate] = useState(new Date());
  const [users, setUsers] = useState([]);
  const [kpi, setKpi] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [kpiPeriodo, setKpiPeriodo] = useState([]);
  const [kpiIncrements, setKpiIncrements] = useState(null);

  const [filteredData, setFilteredData] = useState([]);
  const [selectedRagioneSociale, setSelectedRagioneSociale] = useState(null);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [clientiOptions, setClientiOptions] = useState([]);

  useEffect(async () => {
    let auth = sessionStorage.getItem("Autorizzazione");
    if (auth === "0") {
      setSelectedUser(sessionStorage.getItem("idUsername"));
      loadKPI(sessionStorage.getItem("idUsername"));
      loadKPIPeriodo(
        moment(new Date()).format("MM"),
        moment(new Date()).format("YYYY"),
        sessionStorage.getItem("idUsername"),
      );
    }
    loadUsers();
  }, []);

  useEffect(() => {
    // Filter data based on selected RagioneSociale
    if (selectedRagioneSociale) {
      setFilteredData(
        kpiPeriodo.filter(
          (entry) => entry.RagioneSociale === selectedRagioneSociale,
        ),
      );
    } else {
      setFilteredData(kpiPeriodo); // No filter applied
    }
  }, [selectedRagioneSociale, kpiPeriodo]);

  useEffect(() => {
    // Lista clienti per il select del grafico delle medie, dedotta da kpiPeriodo.
    const seen = new Set();
    const list = [];
    (kpiPeriodo || []).forEach((entry) => {
      if (entry?.Cliente && !seen.has(entry.Cliente)) {
        seen.add(entry.Cliente);
        list.push({ ID: entry.Cliente, RagioneSociale: entry.RagioneSociale });
      }
    });
    list.sort((a, b) =>
      String(a.RagioneSociale || "").localeCompare(b.RagioneSociale || ""),
    );
    setClientiOptions(list);
  }, [kpiPeriodo]);

  const loadUsers = async () => {
    let responseUtenti = await unsplash.get("utenti", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("jwt")}`,
      },
    });
    setUsers(responseUtenti.data.data.filter((us) => us.Medicina === 1));
  };

  const loadKPI = async (user, cliente) => {
    let responseKpi = await unsplash.get("kpi", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("jwt")}`,
      },
      params: {
        user: user,
        tipologia: "Medicina",
        ...(cliente ? { cliente } : {}),
      },
    });
    setKpi(responseKpi.data.data);

    let responseIncremnts = await unsplash.get("kpi", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("jwt")}`,
      },
      params: {
        user: user,
        tipologia: "IncrementsRateMedicina",
        ...(cliente ? { cliente } : {}),
      },
    });

    setKpiIncrements(
      responseIncremnts.data.data.length > 0
        ? responseIncremnts.data.data[0]
        : null,
    );
  };

  const loadKPIPeriodo = async (mese, anno, user) => {
    let responseKpi = await unsplash.get("kpi", {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("jwt")}`,
      },
      params: {
        mese: mese,
        user: user,
        tipologia: "MedicinaPeriodo",
        anno: anno,
      },
    });
    setKpiPeriodo(responseKpi.data.data);
    console.log(responseKpi.data.data);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="label">{`${label}: ${payload[0].value} %`}</p>
        </div>
      );
    }

    return null;
  };

  return (
    <>
      <Card sx={{ boxShadow: "none" }}>
        <CardContent>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              md={3}
              style={{
                display:
                  sessionStorage.getItem("Autorizzazione") === "0"
                    ? "None"
                    : "",
              }}
            >
              <FormControl fullWidth>
                <InputLabel id="demo-simple-select-label">Utente</InputLabel>
                <Select
                  labelId="demo-simple-select-label"
                  id="demo-simple-select"
                  //value={cliente.UtenteAssociato}
                  label="Utente"
                  onChange={(e) => {
                    setSelectedUser(e.target.value);
                    setSelectedCliente(null);
                    loadKPI(e.target.value);
                    loadKPIPeriodo(
                      moment(date).format("MM"),
                      moment(date).format("YYYY"),
                      e.target.value,
                    );
                  }}
                >
                  <MenuItem value={null}>Seleziona...</MenuItem>
                  {users?.map((u) => (
                    <MenuItem value={u.ID}>{u.username}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel id="kpi-cliente-select-label">Cliente</InputLabel>
                <Select
                  labelId="kpi-cliente-select-label"
                  id="kpi-cliente-select"
                  value={selectedCliente ?? ""}
                  label="Cliente"
                  disabled={!selectedUser || clientiOptions.length === 0}
                  onChange={(e) => {
                    const value = e.target.value === "" ? null : e.target.value;
                    setSelectedCliente(value);
                    const matched = clientiOptions.find((c) => c.ID === value);
                    setSelectedRagioneSociale(
                      matched ? matched.RagioneSociale : null,
                    );
                    loadKPI(selectedUser, value);
                  }}
                >
                  <MenuItem value="">Tutti</MenuItem>
                  {clientiOptions.map((c) => (
                    <MenuItem key={c.ID} value={c.ID}>
                      {c.RagioneSociale}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <p
            style={{
              margin: "0",
              fontWeight: 400,
              fontSize: "1.5rem",
              lineHeight: 1.334,
              letterSpacing: "0em",
              display: "block",
            }}
          >
            Indice di accuratezza
          </p>
          <Grid container spacing={2}>
            <Grid xs={0} md={3} />
            <Grid xs={6} md={2} item>
              <center>
                Ultimi 3 mesi <br />
                <small style={{ color: "#6b7280" }}>
                  {kpiIncrements?.MesiAttuali?.length > 0
                    ? kpiIncrements.MesiAttuali.slice().reverse().join(", ")
                    : "—"}
                </small>
                <br />
                <CircularProgressWithLabel
                  variant="determinate"
                  value={
                    kpiIncrements ? kpiIncrements.MediaAttuale?.toFixed(2) : 0
                  }
                  size={100}
                />
              </center>
            </Grid>
            <Grid xs={6} md={2} item>
              <center>
                3 mesi precedenti <br />
                <small style={{ color: "#6b7280" }}>
                  {kpiIncrements?.MesiVecchi?.length > 0
                    ? kpiIncrements.MesiVecchi.slice().reverse().join(", ")
                    : "—"}
                </small>
                <br />
                <CircularProgressWithLabel
                  variant="determinate"
                  value={
                    kpiIncrements ? kpiIncrements.MediaVecchia?.toFixed(2) : 0
                  }
                  size={100}
                />
              </center>
            </Grid>
            <Grid xs={6} md={2} item>
              <center>
                Variazione <br />
                <Box sx={{ position: "relative", display: "inline-flex" }}>
                  <CircularProgress
                    variant="determinate"
                    thickness={7}
                    value={100}
                    size={100}
                    sx={{ visibility: "hidden" }}
                  />
                  <Box
                    sx={{
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                      position: "absolute",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {kpiIncrements ? (
                      kpiIncrements.Stato?.toFixed(2) > 0 ? (
                        <KeyboardDoubleArrowUp
                          fontSize="large"
                          sx={{ color: "#40C18F" }}
                          className="objectArrow"
                        />
                      ) : kpiIncrements.Stato?.toFixed(2) < 0 ? (
                        <KeyboardDoubleArrowDown
                          className="objectArrowDown"
                          fontSize="large"
                          sx={{ color: "#FF2768" }}
                        />
                      ) : (
                        <DragHandle
                          fontSize="large"
                          sx={{ color: "#11526f" }}
                          className="objectArrow"
                        />
                      )
                    ) : (
                      ""
                    )}

                    <Typography
                      component="div"
                      color="text.secondary"
                      variant="h5"
                    >
                      {`${Math.round(
                        kpiIncrements ? kpiIncrements.Stato?.toFixed(2) : 0,
                      )}%`}
                    </Typography>
                  </Box>
                </Box>
              </center>
            </Grid>

            <Grid xs={0} md={3} />
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ width: "100%", height: " 40vh" }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart width={500} height={400} data={kpi}>
                  <CartesianGrid />
                  <XAxis
                    dataKey="Name"
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis domain={[0, 100]} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="Media" barSize={20}>
                    {kpi.map((entry, idx) => (
                      <Cell
                        key={idx}
                        fill={entry?.isLive ? "#FFC897" : "#11526F"}
                        stroke={entry?.isLive ? "#11526F" : undefined}
                        strokeWidth={entry?.isLive ? 2 : 0}
                        strokeDasharray={entry?.isLive ? "4 2" : undefined}
                      />
                    ))}
                  </Bar>
                  <Line type="monotone" dataKey="Media" stroke="#8E75AC" />
                </ComposedChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      <Card sx={{ marginTop: "15px" }}>
        <CardHeader
          title="Dettaglio Periodo selezionato"
          subheader={"Clienti totali periodo: " + kpiPeriodo.length}
        />

        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={DateAdapter}>
                <DatePicker
                  views={["month", "year"]}
                  ampmInClock={false}
                  renderInput={(props) => <TextField fullWidth {...props} />}
                  label="Periodo"
                  value={moment(date)}
                  onChange={(newValue) => {
                    setDate(newValue ? newValue : null);
                    loadKPIPeriodo(
                      newValue.format("MM"),
                      newValue.format("YYYY"),
                      selectedUser,
                    );
                  }}
                />
              </LocalizationProvider>
            </Grid>
            {/* Autocomplete Filter */}
            <Grid item xs={12} md={3}>
              <Autocomplete
                options={kpiPeriodo.map((entry) => entry.RagioneSociale)}
                value={selectedRagioneSociale}
                onChange={(event, newValue) => {
                  setSelectedRagioneSociale(newValue);
                  const matched = clientiOptions.find(
                    (c) => c.RagioneSociale === newValue,
                  );
                  const newCliente = matched ? matched.ID : null;
                  setSelectedCliente(newCliente);
                  if (selectedUser) loadKPI(selectedUser, newCliente);
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Cliente" />
                )}
              />
            </Grid>

            <Grid item xs={12} sx={{ width: "100%", height: "40vh" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart width={500} height={300} data={filteredData}>
                  <CartesianGrid />
                  <XAxis dataKey="RagioneSociale" />
                  <YAxis domain={[0, 100]} />
                  <Tooltip />

                  <Bar dataKey="Percentuale">
                    {filteredData.map((entry, index) => (
                      <Cell fill={entry.color} key={`cell-${index}`} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

function CircularProgressWithLabel(props) {
  return (
    <>
      <Box sx={{ position: "relative", display: "inline-flex" }}>
        <CircularProgress
          variant="determinate"
          thickness={7}
          {...props}
          sx={{
            color:
              props.value <= 30
                ? "#FF2768"
                : props.value <= 60
                  ? "#FFC897"
                  : "#40C18F",
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: "absolute",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography component="div" color="text.secondary" variant="h5">
            {`${Math.round(props.value)}%`}
          </Typography>
        </Box>
      </Box>
    </>
  );
}

export default KpiMedicina;
